import { Atta } from '@/interfaces/IATTA'
import {
	IAttaServices,
	IEntidadeLog,
	IProposta,
	ISelectItemList,
	IStorageDocumento,
	ITipoDocumento,
	IApiResponse,
	IApiPaginatedResponse,
	IPropostaItemList,
	ICorretor,
	Parceiros,
	User,
	IProposalCreateWithoutTriggers,
	Generico
} from '@/interfaces'
import { IPropostas } from '@/interfaces/IPropostas'
import { IParametrizacoes } from '@/interfaces/IParametrizacoes'
import { AttaHttpClientFactory } from './http/AttaHttpClientFactory'
import { UserTypeService } from './UserTypeService'
import { IPropostaObservacao } from '../interfaces/IPropostaObservacao'
import { StorageDocumentoTopicos } from '@/types/StorageDocumentoTopicos'
import { IParceiroRequest } from '@/interfaces/IParceiroRequest'
import { IVinculoResponsavelRequest } from '@/interfaces/IVinculoResponsavelRequest'
import { IUsersFilter } from '@/interfaces/IUsersFilterRequest'
import { CancelTokenSource } from 'axios'
import { IFiduciaryLien } from '@/interfaces/IFiduciaryLien'
import { DateTime } from 'luxon'

const http = AttaHttpClientFactory.create(import.meta.env.VUE_APP_API_ATTA_SERVICES)

export class AttaServices {
	static readonly clientes = {
		async getDetalhes(cpf: String) {
			try {
				const response = await http.get<IAttaServices.IClientesDetalhes>(`/clientes/${cpf}`)
				const { result: cliente } = response.data

				return cliente
			} catch (error) {
				console.error(error)
			}
		}
	}

	/*
	 * Requisições de parametrização
	 */
	static async getCanalParametrizacoes() {
		const response = await http.get<IParametrizacoes.API.Response.IParametrizacoesListResponse>(`/canal/vinculos`)
		return response.data
	}

	/*
	 * REQUISIÇÕES DE PARCEIROS
	 */
	static async getParceiros(params: Parceiros.IParametrosParceirosSearch) {
		const { data } = await http.post('/parceiros/lista', params)
		return data
	}

	static async atualizarParceiros(idParceiro: number, params: any) {
		const { data } = await http.put(`/parceiros/${idParceiro}`, params)
		return data
	}

	static async addParceiros(params: IParceiroRequest) {
		const { data } = await http.put(`/parceiros/cadastro`, params)
		return data
	}

	static async postCanalFranquia(dadosParametrizacao: Atta.API.Request.IIncluirCanalFranquia) {
		const response = await http.post(`/canal/vincular`, dadosParametrizacao)
		return response.data
	}

	static async deleteParametrizacao(parametrizacaoId: number) {
		const response = await http.delete<IParametrizacoes.API.Response.IParametrizacoesListResponse>(`/canal/vinculos/${parametrizacaoId}`)
		return response.data
	}

	static async putParametrizacao(body: IParametrizacoes.IParametrizacoesAtualizacaoItem) {
		const response = await http.put<IParametrizacoes.API.Response.IParametrizacoesListResponse>(`/canal/vinculos`, body)
		return response.data
	}

	static async getPropostas(params: any, cancelationToken: CancelTokenSource | null = null) {
		var response = await http.post<IApiPaginatedResponse<Array<IPropostaItemList>>>('/propostas', params, {
			cancelToken: cancelationToken?.token
		})

		return response.data
	}

	static async getProposta(propostaId: number) {
		const response = await http.get<IPropostas.API.Response.IPropostaResponse>(`/propostas/${propostaId}`)
		return response.data
	}

	static async getFiduciaryLien(proposalId: number) {
		const response = await http.get<IFiduciaryLien>(`/PreAnalysis/${proposalId}/fiduciary-lien`)
		return response.data
	}

	static async getPropostasParametros() {
		const response = await http.get<IPropostas.API.Response.IPropostasParametrosResponse>(`/propostas/parametros`)
		return response.data
	}

	static async getNovaProposta() {
		const response = await http.get<IPropostas.API.Response.IPropostaNovaPropostaResponse>(`/propostas/novaproposta`)
		return response.data
	}
	/**
	 * Lista observações
	 * @param propostaId
	 * @returns TODO
	 */
	static async getPropostaObservacoes(propostaId: number) {
		const { data } = await http.get<IApiResponse<Array<IPropostaObservacao>>>(`/propostas/${propostaId}/observacoes`)
		return data
	}

	/**
	 * Paginated list of proposal observations (most recent first)
	 * @param propostaId
	 * @param page
	 * @param itemsPerPage
	 * @returns Paginated observations
	 */
	static async getPaginatedObservationsByProposalId(propostaId: number, page: number = 1, itemsPerPage: number = 10) {
		const url = `/propostas/v2/${propostaId}/observacoes?page=${page}&itemsPerPage=${itemsPerPage}`
		const { data } = await http.get<IApiPaginatedResponse<IPropostaObservacao>>(url)
		return data
	}
	/**
	 * Adiciona Observação
	 * @param propostaId
	 * @param observacao
	 * @returns TODO
	 */
	static async addPropostaObservacao(propostaId: number, observacao: string) {
		const { data } = await http.post<IApiResponse<IPropostaObservacao>>(`/propostas/${propostaId}/observacao`, {
			propostaId,
			observacao
		})
		return data
	}

	static async setPropostaSituacaoStatus(propostaId: number, situacao: number, status?: number) {
		const { data } = await http.post(`/propostas/${propostaId}/status`, {
			propostaId,
			status,
			situacao
		})
		return data
	}

	static async rollbackPropostaSituacao(propostaId: number) {
		const { data } = await http.post<IApiResponse<IProposta>>(`/propostas/${propostaId}/status/rollback`)
		return data
	}

	static async getDetalheAnalista() {
		const response = await http.get<IPropostas.API.Response.IPropostaNovaPropostaResponse>(`/propostas/detalheanalista`)
		return response.data
	}

	static async getExcelPropostas(params: any) {
		const response = await http.get(`/propostas/exportar-excel`, { params: params, responseType: 'blob' })

		return response.data
	}

	static async exportProposalsExcel(request: Generico.IProposalSearchParams) {
		const response = await http.post(`/propostas/export-excel`, request, { responseType: 'blob' })

		return response.data
	}

	static async getListaDocumentos() {
		var response = await http.get<IApiResponse<Array<ITipoDocumento>>>('/file/tipos-documentos')
		return response.data
	}

	static async getClienteDocumentos(cpf: string) {
		var response = await http.get<Atta.API.Response.IClienteListaDocumentos>(`/file/documentos/${cpf}`)
		return response.data
	}

	/**
	 * Retorna uma lista de cidades pelo estado solicitado
	 */
	static async getCidadesByEstado(siglaEstado: string) {
		var response = await http.get<Atta.API.Response.IListaCidades>(`/ibge/estados/${siglaEstado}/municipios`)
		return response.data
	}

	static buildDownloadUrl(file: string, directDownload: boolean = false) {
		if (file) {
			const baseUrl = UserTypeService.isInternal()
				? import.meta.env.VUE_APP_API_BACKOFFICE_BFF
				: import.meta.env.VUE_APP_API_PORTFOLIO_MANAGER
			return `${baseUrl}/v1/isolve/api-service/file/download?file=${file}&directDownload=${directDownload}`
		}
	}

	/**
	 * @param propostaId
	 * @returns TODO
	 */
	static async getPropostaLogs(propostaId: number) {
		const response = await http.get<IApiResponse<Array<IEntidadeLog>>>(`/propostas/${propostaId}/logs`)
		return response.data
	}

	static async getDocumentosByEntidadeId(entidadeId: string, topicos: Array<StorageDocumentoTopicos>, propostaId?: number) {
		var response = await http.get<IApiResponse<Array<IStorageDocumento>>>(`/file/v2/documentos/${entidadeId}`, {
			params: { topicos, propostaId }
		})
		return response.data
	}
	/**
	 * @param documentId
	 * @param status
	 * @returns TODO
	 */
	static async postStatusDocument(documentId: number | string, status: number) {
		const { data } = await http.post<IStorageDocumento>(`/file/${documentId}/status/${status}`)
		return data
	}

	/**
	 * Upload Documento
	 * @param cpf
	 * @param tipoDocumento
	 * @param file
	 * @param preAnalysisId
	 * @returns TODO
	 */
	static async postDocumentoUpload(file: any, entidadeId: any, topico: number, tipoDocumento: number, preAnalysisId?: number) {
		let formData = new FormData()

		formData.append('file', file)
		formData.append('entidadeId', entidadeId)
		formData.append('topico', topico.toString())
		formData.append('tipoDocumento', tipoDocumento.toString())
		if (preAnalysisId) formData.append('preAnalysisId', preAnalysisId.toString())

		const { data } = await http.post<IApiResponse<IStorageDocumento>>(`/file/document-upload`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
		return data
	}

	/**
	 * Retorna uma lista de hubs cadastradas para corretores
	 * @returns
	 */
	static async getCorretorHubs() {
		const response = await http.get<IApiResponse<Array<ISelectItemList>>>('/Corretores/hubs')
		return response.data
	}

	/**
	 * Retorna uma lista de corretores cadastrados
	 * @returns
	 */
	static async getCorretores() {
		const response = await http.get<IApiPaginatedResponse<ICorretor>>('/Corretores')
		return response.data
	}

	/**
	 *
	 * @param files Array de keys do storageDocumentos, lembrando q o array de keys vem no formato de url formada do s3.
	 * @returns retorna 1 ou n arquivos na interface Blob
	 * Ps : Existe uma função chamada forceDownloadFile em util/index.ts que foi criada ser realizado o download diretamente na máquina do usuário.
	 */
	static async getDownloadFilesAsync(files: string[], downloadFileName: string) {
		if (files.length > 1) {
			let stringArray = files.join('&files=')
			const response = await http.get<Blob>(`/file/download?files=${stringArray}&downloadFileName=${downloadFileName}`, {
				responseType: 'arraybuffer'
			})
			return response.data
		}

		if (files.length == 1) {
			const responseUnico = await http.get<Blob>(
				`/file/download?file=${files[0]}&downloadFileName=${downloadFileName}&directDownload=true`,
				{
					responseType: 'arraybuffer'
				}
			)
			return responseUnico.data
		}
	}

	/**
	 * Retorna documentos por parametoros
	 */
	static async getTipoDocumentosByParametrosAsync(idFornecedor: number, idTopico: number, idProduto: number) {
		const response = await http.get<IApiResponse<Array<ITipoDocumento>>>(
			`/tipo-documento/parametros?fornecedorId=${idFornecedor}&topicoId=${idTopico}&produtoId=${idProduto}`
		)
		return response.data.result
	}

	// Retorna lista de parceiros por responsavel
	static async getParceirosByResponsavel(responsavelId: number) {
		const response = await http.get<IApiResponse<Array<Parceiros.IDetalheParceiro>>>(`/parceiros/lista/responsavel/${responsavelId}`)
		return response.data.result
	}

	// Envia parceiros para serem vinculados a responsável
	static async postVinculoParceiroResponsavel(request: IVinculoResponsavelRequest) {
		const response = await http.post<IApiResponse<any>>(`/parceiros/vincular`, request)
		return response
	}

	// Return detailed user info by id
	static async getUserById(id: number) {
		const response = await http.get<IApiResponse<User.IUserDetails>>(`/user/${id}`)
		return response.data.result
	}

	// Add or update user
	static async addOrUpdateUser(user: User.IUserDetails) {
		const response = await http.post<IApiResponse<User.IUserDetails>>(`/user`, user)
		return response.data.result
	}

	static async checkCPFAndEmail(userId: number, field: string, value: string) {
		const response = await http.get<IApiResponse<boolean>>(`/user/check?userId=${userId}&field=${field}&value=${value}`)
		return response.data.result
	}

	/**
	 * Update current user Avatar
	 * @param userId
	 * @param avatar
	 * @returns
	 */
	static async UpdateCurrentUserAvatar(userId: number, avatar: string) {
		const response = await http.post<IApiResponse<string>>(`/user/avatar`, {
			email: 'avatar@avatar.com',
			nome: 'Update Avatar Action',
			pathAvatar: avatar
		})
		return response.data.result
	}

	/**
	 * Get a user avatar by identity
	 * @param userId
	 * @returns
	 */
	static async getUserAvatarByUserId(userId: number) {
		const response = await http.get<IApiResponse<string>>(`/user/${userId}/avatar`)
		return JSON.parse(response.data.result) as User.IUserAvatar
	}

	/**
	 * Return paginated user list filtered by request
	 * @param params
	 * @returns
	 */
	static async getUsersListAsync(params: IUsersFilter.IUserListFilterRequest) {
		var response = await http.post('/user/users-list', params)
		return response.data.result.usuarios
	}

	/**
	 * Return franchise category
	 * @param id
	 * @returns
	 */
	static async getFranchiseCategory(id: number) {
		var response = await http.get(`/franquias/${id}/categoria`)
		return response.data.result
	}

	/**
	 * Download PreAnalysis pdf forms
	 * @param id
	 * @returns
	 */
	static async downloadPreAnalysisPdfForm(consultaScoreId: number, fornecedorId: number) {
		var response = await http.get<Blob>(`/pdf/download-preanalysis-pdf/${consultaScoreId}/${fornecedorId}`, {
			responseType: 'arraybuffer'
		})
		return response.data
	}

	/**
	 * Update user last changelog seen
	 * @returns
	 */
	static async updateLastChangelogSeen() {
		var response = await http.post<IApiResponse<string>>(`/user/changelog-seen`)
		return response.data.result
	}

	/**
	 * Get user last changelog seen
	 * @returns
	 */
	static async getLastChangelogSeen() {
		var response = await http.get<IApiResponse<string>>(`/user/changelog-seen`)
		return response.data.result
	}

	/**
	 * Get if franchise is despachante
	 * @param bankId
	 * @returns
	 */
	static async getFranchiseDespachante(franchiseId: number) {
		var response = await http.get<IApiResponse<boolean>>(`/franquias/${franchiseId}/is-despachante`)

		if (response.data.error) return false

		return response.data.result
	}

	/**
	 * Create proposal without triggers
	 * @param params
	 * @returns
	 */
	static async createProposalWithoutTriggers(params: IProposalCreateWithoutTriggers) {
		// Format DataIQ to simplified ISO date (yyyy-MM-dd) for the Atta call
		var dataIQ = params.confValRequest.DataIQ
		if (dataIQ == null || dataIQ === '') {
			params.confValRequest.DataIQ = null
		} else if (typeof dataIQ === 'string') {
			const fromBr = DateTime.fromFormat(dataIQ, 'dd/MM/yyyy')
			if (fromBr.isValid) {
				params.confValRequest.DataIQ = fromBr.toISODate()
			} else {
				const fromIso = DateTime.fromISO(dataIQ)
				params.confValRequest.DataIQ = fromIso.isValid ? fromIso.toISODate() : null
			}
		}

		var response = await http.post('/propostas/create-without-triggers', params)
		return response.data
	}

	/**
	 * Check if user should update phone
	 * @returns
	 */
	static async getShouldUserUpdatePhone() {
		var response = await http.get<IApiResponse<boolean>>(`/user/phone-update-required`)
		return response.data.result
	}

	/**
	 * Handle mandatory phone update
	 * @returns
	 */
	static async updatePhone(params: User.IUserPhoneUpdate) {
		var response = await http.post<IApiResponse<object>>(`/user/phone-update`, params)
		return response.data
	}

	static async getUserAccounts() {
		const response = await http.get<IApiResponse<Array<User.IUserAccount>>>('/user/me')
		return response.data.result
	}
}
